import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { ApiError, currentTenant, setTenant } from '../lib/api';
import { Logo } from '../components/Logo';
import { Icon } from '../components/Icon';
import { isLocalHost } from '../lib/site';

/*
 * «تذكّرني» يحفظ البريدَ والحسابَ وكلمةَ المرور في هذا المتصفّح فقط ليُملأ
 * كلُّه تلقائيًّا في الزيارة القادمة — كما يفعل «حفظ كلمة المرور» في المتصفّح.
 *
 * كلمةُ المرور تُخزَّن مموَّهةً (Base64) لا مشفّرةً تشفيرًا حقيقيًّا: من يصل
 * إلى تخزينِ هذا المتصفّحِ يصلُ إليها، فهي راحةٌ على جهازٍ موثوقٍ لا حمايةٌ
 * أمنيّة. من يشارك جهازَه فليُبقِ «تذكّرني» بلا تحديد.
 */
const REMEMBER_KEY = 'goyone_remember';

interface Remembered {
  email: string;
  tenant: string;
  pw?: string;
}

/** تمويهٌ خفيفٌ لكلمةِ المرورِ (لا تشفير) — يتحمّل أحرفَ اليونيكود. */
function encPw(v: string): string {
  try {
    return btoa(unescape(encodeURIComponent(v)));
  } catch {
    return '';
  }
}
function decPw(v: string | undefined): string {
  if (!v) return '';
  try {
    return decodeURIComponent(escape(atob(v)));
  } catch {
    return '';
  }
}

function readRemember(): Remembered | null {
  try {
    const raw = localStorage.getItem(REMEMBER_KEY);
    return raw ? (JSON.parse(raw) as Remembered) : null;
  } catch {
    return null;
  }
}

export function Login() {
  const { login, verifyLogin } = useAuth();
  const remembered = readRemember();
  const [email, setEmail] = useState(remembered?.email ?? '');
  const [password, setPassword] = useState(decPw(remembered?.pw));
  const [tenantSlug, setTenantSlug] = useState(
    currentTenant() ?? remembered?.tenant ?? '',
  );
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /* تحدّي جهازٍ جديد — يُطلب رمزُ تأكيدٍ قبل الدخول. */
  const [challenge, setChallenge] = useState<null | {
    method: 'totp' | 'email';
    challengeToken: string;
    email?: string;
    devCode?: string;
  }>(null);
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(true);

  const rememberCreds = () => {
    try {
      if (remember) {
        localStorage.setItem(
          REMEMBER_KEY,
          JSON.stringify({
            email: email.trim(),
            tenant: tenantSlug.trim(),
            pw: encPw(password),
          } satisfies Remembered),
        );
      } else {
        localStorage.removeItem(REMEMBER_KEY);
      }
    } catch {
      /* التخزينُ قد يُمنع (تصفّحٌ خاصّ) — لا يُعطّل الدخول. */
    }
  };

  /*
   * حقل الحساب يظهر على localhost فقط.
   *
   * في الإنتاج لا تُعرض هذه الشاشة إلا على نطاق الشركة (sunmed.goyone.com)،
   * وهو يحدّدها؛ والموقع العامّ يعرض «ادخل إلى شركتك» بدلها. أما في التطوير
   * فلا نطاق فرعي، فلا بد من تحديدها يدويًا.
   */
  const needsTenantField = isLocalHost();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (needsTenantField && tenantSlug.trim()) setTenant(tenantSlug.trim());
      const outcome = await login(email, password);
      /* الكلمةُ صحّت — نتذكّر البريدَ والحسابَ وكلمةَ المرور إن طُلب «تذكّرني». */
      rememberCreds();
      if ('challenge' in outcome) {
        setChallenge({
          method: outcome.challenge,
          challengeToken: outcome.challengeToken,
          email: outcome.email,
          devCode: outcome.devCode,
        });
        setCode('');
        setBusy(false);
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'تعذّر الاتصال بالخادم. تحقق من الشبكة.',
      );
      setBusy(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setBusy(true);
    try {
      await verifyLogin(challenge.challengeToken, code.trim(), trustDevice);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'تعذّر التحقّق. حاول مرّةً أخرى.');
      setBusy(false);
    }
  }

  if (challenge) {
    return (
      <div className="login">
        <form className="login-card" onSubmit={onVerify} noValidate>
          <div className="login-brand">
            <Logo size={34} />
            <span>GoyOne</span>
          </div>
          <p className="login-sub">تأكيد الدخول من جهازٍ جديد</p>

          {error && (
            <div className="alert alert-danger mb-4" role="alert">
              {error}
            </div>
          )}

          <div className="login-form">
            <p className="small mute" style={{ margin: 0 }}>
              {challenge.method === 'totp'
                ? 'أدخل الرمز الظاهر في تطبيق المصادقة على هاتفك.'
                : `أرسلنا رمزًا إلى بريدك${challenge.email ? ` (${challenge.email})` : ''} — أدخله للمتابعة.`}
            </p>
            {challenge.devCode && (
              <p className="tiny" style={{ color: 'var(--warning)', margin: 0 }}>
                لأغراض التطوير — الرمز: <b className="num">{challenge.devCode}</b>
              </p>
            )}
            <div className="field">
              <label className="label req" htmlFor="otp">
                رمز التأكيد
              </label>
              <input
                id="otp"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                maxLength={6}
                value={code}
                placeholder="••••••"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{ letterSpacing: '0.3em', textAlign: 'center' }}
                autoFocus
              />
            </div>

            <label className="login-remember">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
              />
              <span>الثقة بهذا الجهاز — لا تطلب الرمز عليه لاحقًا</span>
            </label>

            <button className="btn btn-primary" type="submit" disabled={busy || code.length !== 6}>
              {busy ? 'جارٍ التحقق' : 'تأكيد الدخول'}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => {
                setChallenge(null);
                setError(null);
              }}
            >
              رجوع
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <div className="login-brand">
          <Logo size={34} />
          <span>GoyOne</span>
        </div>
        <p className="login-sub">نظام إدارة الأعمال المتكامل</p>

        {error && (
          <div className="alert alert-danger mb-4" role="alert">
            {error}
          </div>
        )}

        <div className="login-form">
          {needsTenantField && (
            <div className="field">
              <label className="label" htmlFor="tenant">
                معرّف الحساب
              </label>
              <input
                id="tenant"
                className="input"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                placeholder="demo"
                autoComplete="organization"
                dir="ltr"
              />
              <span className="tiny mute">
                في بيئة التشغيل يُحدَّد الحساب من نطاقه، وهذا الحقل للتطوير المحلي.
              </span>
            </div>
          )}

          <div className="field">
            <label className="label req" htmlFor="email">
              البريد الإلكتروني
            </label>
            <input
              id="email"
              className="input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="username"
              autoFocus
              dir="ltr"
            />
          </div>

          <div className="field">
            <label className="label req" htmlFor="password">
              كلمة المرور
            </label>
            <div className="pw-input">
              <input
                id="password"
                className="input"
                type={showPw ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                dir="ltr"
              />
              <button
                type="button"
                className="pw-eye"
                onClick={() => setShowPw((v) => !v)}
                title={showPw ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                aria-label={showPw ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                aria-pressed={showPw}
              >
                <Icon name={showPw ? 'eye' : 'eye'} size={16} />
              </button>
            </div>
          </div>

          <label className="login-remember">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span>تذكّرني على هذا الجهاز</span>
          </label>

          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? 'جارٍ التحقق' : 'تسجيل الدخول'}
          </button>
          <Link to="/forgot" className="tiny" style={{ textAlign: 'center' }}>
            نسيت كلمة المرور؟
          </Link>
        </div>
      </form>
    </div>
  );
}
